import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Mail, Star, Users, Trophy, Music, Copy, ChevronDown, X, ChevronLeft, ChevronRight, Upload, AlertCircle, Clock, FileText } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

const packages = [
  { 
    id: "male-tshirt", 
    name: "Male T-Shirt", 
    price: 280,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/7zht2rgg_06053064-9D2B-4FDE-8618-E9E6202570DE.png",
    includes: ["T-Shirt", "Wristband", "Breakfast", "Lunch"]
  },
  { 
    id: "female-tshirt", 
    name: "Female T-Shirt", 
    price: 280,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/zj4r7mg6_5C1CA8EB-F85B-4BB2-A5AE-ECA94D34A8F9.png",
    includes: ["T-Shirt", "Wristband", "Breakfast", "Lunch"]
  },
  { 
    id: "short-sleeve-monokini", 
    name: "Short Sleeve Monokini", 
    price: 320,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/9tpoawx6_EF44CA42-1B06-4303-8A31-F808709A0463.png",
    includes: ["Short Sleeve Monokini", "Wristband", "Breakfast", "Lunch"]
  },
  { 
    id: "long-sleeve-monokini", 
    name: "Long Sleeve Monokini", 
    price: 320,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/pyx4289t_01D2DB41-1064-4DD4-95C8-FFA88AE29685.png",
    includes: ["Long Sleeve Monokini", "Wristband", "Breakfast", "Lunch"]
  },
  { 
    id: "all-inclusive-male-tshirt", 
    name: "All Inclusive Male T-Shirt", 
    price: 550,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/7zht2rgg_06053064-9D2B-4FDE-8618-E9E6202570DE.png",
    includes: ["T-Shirt", "Wristband", "Breakfast", "Lunch", "Unlimited Drinks", "Unlimited Food"]
  },
  { 
    id: "all-inclusive-female-tshirt", 
    name: "All Inclusive Female T-Shirt", 
    price: 550,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/zj4r7mg6_5C1CA8EB-F85B-4BB2-A5AE-ECA94D34A8F9.png",
    includes: ["T-Shirt", "Wristband", "Breakfast", "Lunch", "Unlimited Drinks", "Unlimited Food"]
  },
  { 
    id: "all-inclusive-short-sleeve-monokini", 
    name: "All Inclusive Short Sleeve Monokini", 
    price: 550,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/9tpoawx6_EF44CA42-1B06-4303-8A31-F808709A0463.png",
    includes: ["Short Sleeve Monokini", "Wristband", "Breakfast", "Lunch", "Unlimited Drinks", "Unlimited Food"]
  },
  { 
    id: "all-inclusive-long-sleeve-monokini", 
    name: "All Inclusive Long Sleeve Monokini", 
    price: 550,
    image: "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/pyx4289t_01D2DB41-1064-4DD4-95C8-FFA88AE29685.png",
    includes: ["Long Sleeve Monokini", "Wristband", "Breakfast", "Lunch", "Unlimited Drinks", "Unlimited Food"]
  },
];

const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL"];

// Default gallery images (used if none in database)
const defaultGalleryImages = [
  "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/7zht2rgg_06053064-9D2B-4FDE-8618-E9E6202570DE.png",
  "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/pyx4289t_01D2DB41-1064-4DD4-95C8-FFA88AE29685.png",
  "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/9tpoawx6_EF44CA42-1B06-4303-8A31-F808709A0463.png",
  "https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/zj4r7mg6_5C1CA8EB-F85B-4BB2-A5AE-ECA94D34A8F9.png",
];

 

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    email: "",
    package: "",
    size: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [inventory, setInventory] = useState({});
  const [paymentProof, setPaymentProof] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({ downpayment_amount: 100, pending_ttl_hours: 24 });

  // Map packages to their base costume type
  const PACKAGE_TO_COSTUME = {
    "Male T-Shirt": "Male T-Shirt",
    "Female T-Shirt": "Female T-Shirt",
    "Short Sleeve Monokini": "Short Sleeve Monokini",
    "Long Sleeve Monokini": "Long Sleeve Monokini",
    "All Inclusive Male T-Shirt": "Male T-Shirt",
    "All Inclusive Female T-Shirt": "Female T-Shirt",
    "All Inclusive Short Sleeve Monokini": "Short Sleeve Monokini",
    "All Inclusive Long Sleeve Monokini": "Long Sleeve Monokini"
  };

  // Fetch gallery, inventory, payment info, and payment config on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, inventoryRes, paymentRes, configRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/inventory`),
          axios.get(`${API}/settings/payment`),
          axios.get(`${API}/config/payment`)
        ]);
        
        // Use fetched gallery or default
        if (galleryRes.data && galleryRes.data.length > 0) {
          setGalleryImages(galleryRes.data.map(img => getImageUrl(img.url)));
        } else {
          setGalleryImages(defaultGalleryImages);
        }
        
        // Build inventory map using "available" field
        const inventoryMap = {};
        inventoryRes.data.forEach((item) => {
          const key = `${item.package}|${item.size}`;
          inventoryMap[key] = item.available || 0;
        });
        setInventory(inventoryMap);

        if (paymentRes.data) setPaymentInfo(paymentRes.data);
        if (configRes.data) setPaymentConfig(configRes.data);
      } catch (error) {
        // Use defaults on error
        setGalleryImages(defaultGalleryImages);
      }
    };
    fetchData();
  }, []);

  // Get base costume type for a package
  const getCostumeType = (packageName) => {
    return PACKAGE_TO_COSTUME[packageName] || packageName;
  };

  // Check if a package/size combo is in stock
  const isInStock = (packageName, size) => {
    const costumeType = getCostumeType(packageName);
    const key = `${costumeType}|${size}`;
    return (inventory[key] || 0) > 0;
  };

  // Get available quantity
  const getStock = (packageName, size) => {
    const costumeType = getCostumeType(packageName);
    const key = `${costumeType}|${size}`;
    return inventory[key] || 0;
  };

  // Check if any size is available for a package
  const hasAnyStock = (packageName) => {
    return sizes.some(size => isInStock(packageName, size));
  };

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.contact || !formData.package || !formData.size) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!paymentProof) {
      toast.error(`Please upload your $${paymentConfig.downpayment_amount} downpayment proof to confirm registration`);
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("contact", formData.contact);
      fd.append("email", formData.email || "");
      fd.append("package", formData.package);
      fd.append("size", formData.size);
      fd.append("notes", formData.notes || "");
      fd.append("payment_proof", paymentProof);

      const response = await axios.post(`${API}/register`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setRegistered(response.data);
      toast.success("Registration submitted! Awaiting payment verification.");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPaymentProof(null);
      return;
    }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload an image (JPEG/PNG/GIF/WebP) or PDF");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      e.target.value = "";
      return;
    }
    setPaymentProof(file);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "package") {
      const pkg = packages.find(p => p.name === value);
      setSelectedPackage(pkg);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (registered) {
    return (
      <div className="min-h-screen hero-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card className="glass border-primary/30" data-testid="registration-success-card">
            <CardContent className="pt-6 space-y-6">
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="mx-auto mb-4"
                >
                  <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center glow-cyan mx-auto">
                    <CheckCircle2 className="w-10 h-10 text-primary" />
                  </div>
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-1">Registration Submitted!</h2>
                <p className="text-muted-foreground text-sm">Awaiting payment verification</p>
              </div>

              <div className="bg-[#FFC107]/10 p-4 rounded-lg border border-[#FFC107]/30 text-center">
                <Clock className="w-5 h-5 text-[#FFC107] mx-auto mb-2" />
                <p className="text-sm text-[#FFC107] font-semibold mb-1">PENDING VERIFICATION</p>
                <p className="text-xs text-muted-foreground">
                  Our team will verify your ${paymentConfig.downpayment_amount} downpayment within {paymentConfig.pending_ttl_hours} hours.
                  If proof is not verified in time, this registration will be cancelled and your spot released.
                </p>
              </div>

              <div className="text-center">
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-1">
                  Your Registration Number
                </p>
                <p className="text-5xl font-black text-primary neon-cyan" data-testid="reg-number">
                  #{String(registered.reg_number).padStart(3, "0")}
                </p>
              </div>

              <div className="flex justify-center">
                <div className="qr-container" data-testid="qr-code-display">
                  <QRCodeSVG
                    value={registered.id}
                    size={180}
                    level="H"
                    includeMargin={false}
                  />
                </div>
              </div>

              {registered.email ? (
                <div className="bg-muted/30 p-4 rounded-lg text-center">
                  <Mail className="w-5 h-5 text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Confirmation will be emailed to <span className="text-white">{registered.email}</span> once your payment is verified
                  </p>
                </div>
              ) : (
                <div className="bg-[#FF0055]/10 p-4 rounded-lg text-center border border-[#FF0055]/30">
                  <p className="text-sm text-[#FF0055]">
                    No email provided - Please save your registration number and QR code!
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-muted-foreground uppercase tracking-wider text-xs mb-1">Package</p>
                  <p className="text-white font-medium" data-testid="reg-package">{registered.package}</p>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-muted-foreground uppercase tracking-wider text-xs mb-1">Amount Due</p>
                  <p className="text-[#CCFF00] font-bold text-lg" data-testid="reg-amount">${registered.amount_due}</p>
                </div>
              </div>

              <Button
                onClick={() => {
                  setRegistered(null);
                  setFormData({ name: "", contact: "", email: "", package: "", size: "", notes: "" });
                  setSelectedPackage(null);
                  setPaymentProof(null);
                }}
                variant="outline"
                className="w-full border-primary/30 hover:bg-primary/10"
                data-testid="register-another-btn"
              >
                Register Another Person
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex flex-col items-center justify-center p-4 md:p-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring" }}
            className="mb-6"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_48424d6e-4321-4c6e-9b4c-846fc9f102c2/artifacts/sd5kx5d4_fa8a3d51-2da5-421b-90b6-a568e84599e3.jpeg" 
              alt="Soca Jumbies Logo" 
              className="w-56 md:w-72 h-auto mx-auto rounded-2xl drop-shadow-[0_0_25px_rgba(255,0,170,0.5)]"
              style={{ mixBlendMode: 'screen' }}
              data-testid="band-logo"
            />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-black gradient-text mb-4" data-testid="page-title">
            SOCA JUMBIES
          </h1>
          <p className="text-xl md:text-2xl text-white mb-2">
            Tunapuna's Biggest Jouvert Band
          </p>
          <p className="text-muted-foreground text-lg mb-8">
            Premium Quality • Most Experienced • Maximum Vibes
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              onClick={() => scrollToSection('register')}
              className="bg-primary text-black font-bold text-lg px-8 py-6 btn-glow"
            >
              REGISTER NOW
            </Button>
            <Button 
              onClick={() => scrollToSection('about')}
              variant="outline"
              className="border-white/30 text-white font-bold text-lg px-8 py-6"
            >
              LEARN MORE
            </Button>
          </div>
        </motion.div>

        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 cursor-pointer"
          onClick={() => scrollToSection('about')}
        >
          <ChevronDown className="w-8 h-8 text-white/50" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 md:px-8 bg-background noise-overlay">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">WHY SOCA JUMBIES?</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We are the band with the most vibes in Tunapuna! Experience premium quality costumes, 
              top-tier organization, and an unforgettable Jouvert experience with the most experienced team on the road.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Trophy, title: "Most Experienced", desc: "Years of delivering the best Jouvert experience in Tunapuna" },
              { icon: Star, title: "Premium Quality", desc: "Top-quality costumes, drinks, and service for all our masqueraders" },
              { icon: Music, title: "Maximum Vibes", desc: "The best DJs, music trucks, and non-stop energy on the road" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass border-white/10 h-full card-hover">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 glow-cyan">
                      <item.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4 md:px-8 bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">GALLERY</h2>
            <p className="text-muted-foreground text-lg">Check out our costumes and past events - Click to view</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="aspect-square rounded-lg overflow-hidden cursor-pointer group relative"
                onClick={() => openLightbox(index)}
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={img} 
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-bold">View</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Register Now Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-muted-foreground text-lg mb-4">Ready to join the band?</p>
            <Button
              size="lg"
              onClick={() => document.getElementById('register').scrollIntoView({ behavior: 'smooth' })}
              className="bg-primary hover:bg-primary/90 text-black font-bold px-8 py-6 text-lg"
              data-testid="gallery-register-btn"
            >
              REGISTER NOW
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-50"
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Previous Button */}
            <button
              className="absolute left-4 text-white/70 hover:text-white p-2 z-50"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            {/* Image */}
            <motion.img
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={galleryImages[lightboxIndex]}
              alt={`Gallery ${lightboxIndex + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />

            {/* Next Button */}
            <button
              className="absolute right-4 text-white/70 hover:text-white p-2 z-50"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
              {lightboxIndex + 1} / {galleryImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Registration Section */}
      <section id="register" className="py-20 px-4 md:px-8 bg-muted/10">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-black gradient-text mb-4">REGISTER NOW</h2>
            <p className="text-muted-foreground">Secure your spot in Tunapuna's biggest Jouvert band</p>
          </motion.div>

          <Card className="glass border-white/10" data-testid="registration-form-card">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Full Name
                  </Label>
                  <Input
                    data-testid="input-name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Contact Number
                  </Label>
                  <Input
                    data-testid="input-contact"
                    placeholder="Your phone number"
                    value={formData.contact}
                    onChange={(e) => handleChange("contact", e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Email Address <span className="text-muted-foreground/60">(Optional - for confirmation)</span>
                  </Label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  />
                </div>

                {/* Package Selection with Images */}
                <div className="space-y-3">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Select Package
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {packages.map((pkg) => {
                      const outOfStock = !hasAnyStock(pkg.name);
                      return (
                        <motion.div
                          key={pkg.id}
                          whileHover={!outOfStock ? { scale: 1.02 } : {}}
                          whileTap={!outOfStock ? { scale: 0.98 } : {}}
                          onClick={() => !outOfStock && handleChange("package", pkg.name)}
                          className={`rounded-lg overflow-hidden border-2 transition-all ${
                            outOfStock 
                              ? "border-white/5 opacity-50 cursor-not-allowed"
                              : formData.package === pkg.name 
                                ? "border-primary glow-cyan cursor-pointer" 
                                : "border-white/10 hover:border-white/30 cursor-pointer"
                          }`}
                          data-testid={`package-${pkg.id}`}
                        >
                          <div className="aspect-square relative">
                            <img 
                              src={pkg.image} 
                              alt={pkg.name}
                              className={`w-full h-full object-cover ${outOfStock ? "grayscale" : ""}`}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <p className="text-white font-bold text-sm leading-tight">{pkg.name}</p>
                              <p className="text-[#CCFF00] font-black text-lg">${pkg.price}</p>
                            </div>
                            {outOfStock && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="bg-[#FF0055] text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                                  Sold Out
                                </span>
                              </div>
                            )}
                            {formData.package === pkg.name && !outOfStock && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle2 className="w-6 h-6 text-primary" />
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Selected Package Summary with Details */}
                {selectedPackage && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-primary/10 border border-primary/30 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Selected Package</p>
                        <p className="text-white font-bold">{selectedPackage.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Amount</p>
                        <p className="text-[#CCFF00] font-black text-2xl">${selectedPackage.price}</p>
                      </div>
                    </div>
                    {selectedPackage.includes && (
                      <div className="pt-3 border-t border-primary/20">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Package Includes:</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedPackage.includes.map((item, idx) => (
                            <span 
                              key={idx}
                              className="text-xs bg-white/10 text-white px-2 py-1 rounded-full"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Size {formData.package && <span className="text-primary">(Select package first)</span>}
                  </Label>
                  <Select
                    value={formData.size}
                    onValueChange={(value) => handleChange("size", value)}
                    disabled={!formData.package}
                  >
                    <SelectTrigger
                      data-testid="select-size"
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    >
                      <SelectValue placeholder={formData.package ? "Select your size" : "Select a package first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {sizes.map((size) => {
                        const stock = formData.package ? getStock(formData.package, size) : 0;
                        const available = stock > 0;
                        return (
                          <SelectItem 
                            key={size} 
                            value={size} 
                            disabled={!available}
                            data-testid={`size-option-${size.toLowerCase()}`}
                            className={!available ? "opacity-50" : ""}
                          >
                            {size} {!available ? "(Sold Out)" : stock <= 3 ? `(${stock} left)` : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground">
                    Notes (Optional)
                  </Label>
                  <Textarea
                    data-testid="input-notes"
                    placeholder="Any special requests or notes..."
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    className="bg-muted/50 border-transparent focus:border-primary min-h-[80px] resize-none"
                  />
                </div>

                {/* Downpayment Instructions */}
                <div className="rounded-lg border border-[#CCFF00]/30 bg-[#CCFF00]/5 p-4 space-y-3" data-testid="downpayment-instructions">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-[#CCFF00]" />
                    <p className="text-[#CCFF00] font-bold text-sm uppercase tracking-wider">
                      ${paymentConfig.downpayment_amount} Downpayment Required
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    To confirm your registration, send a <span className="text-white font-semibold">${paymentConfig.downpayment_amount} downpayment</span> via Wire Transfer or PayPal, then upload your payment receipt below. Registration is held for {paymentConfig.pending_ttl_hours} hours pending verification.
                  </p>

                  {paymentInfo && (
                    <div className="space-y-2 pt-2 border-t border-[#CCFF00]/20">
                      {paymentInfo.bank_name && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground">Bank:</span>
                          <span className="text-white font-medium">{paymentInfo.bank_name}</span>
                        </div>
                      )}
                      {paymentInfo.account_name && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground">Account Name:</span>
                          <span className="text-white font-medium">{paymentInfo.account_name}</span>
                        </div>
                      )}
                      {paymentInfo.account_number && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground">Account #:</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(paymentInfo.account_number, "account")}
                            className="text-white font-medium flex items-center gap-1 hover:text-primary"
                            data-testid="copy-account-btn"
                          >
                            {paymentInfo.account_number}
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      {paymentInfo.payment_link && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground">PayPal:</span>
                          <a
                            href={paymentInfo.payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary font-medium hover:underline"
                            data-testid="paypal-link"
                          >
                            Pay via PayPal
                          </a>
                        </div>
                      )}
                      {paymentInfo.contact_number && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-muted-foreground">Questions? Call:</span>
                          <span className="text-white font-medium">{paymentInfo.contact_number}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment Proof Upload */}
                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-muted-foreground flex items-center gap-2">
                    <Upload className="w-3 h-3" /> Upload Payment Proof <span className="text-[#FF0055]">*</span>
                  </Label>
                  <div className="relative">
                    <input
                      id="payment-proof-input"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleProofChange}
                      className="hidden"
                      data-testid="input-payment-proof"
                    />
                    <label
                      htmlFor="payment-proof-input"
                      className={`flex items-center gap-3 h-12 px-4 rounded-md border-2 border-dashed cursor-pointer transition-colors ${
                        paymentProof
                          ? "border-[#CCFF00] bg-[#CCFF00]/5"
                          : "border-white/20 hover:border-primary/50 bg-muted/30"
                      }`}
                    >
                      {paymentProof ? (
                        <>
                          <FileText className="w-5 h-5 text-[#CCFF00]" />
                          <span className="text-white text-sm flex-1 truncate" data-testid="proof-filename">
                            {paymentProof.name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {(paymentProof.size / 1024).toFixed(0)} KB
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-muted-foreground" />
                          <span className="text-muted-foreground text-sm">
                            Click to upload receipt (JPG, PNG, PDF — max 10MB)
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="submit-btn"
                  disabled={loading || !formData.package || !formData.size || !isInStock(formData.package, formData.size) || !paymentProof}
                  className="w-full h-12 bg-primary text-primary-foreground font-bold text-lg btn-glow hover:bg-primary/90"
                >
                  {loading ? "Submitting..." : `JOIN THE BAND — $${paymentConfig.downpayment_amount} DEPOSIT`}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <a
              href="/admin/login"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="admin-link"
            >
              Admin Login →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-background border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-muted-foreground text-sm">
            © 2026 Soca Jumbies. Tunapuna's Biggest Jouvert Band.
          </p>
        </div>
      </footer>
    </div>
  );
}
